/*
 * DS_09 — Gallery video (Contoured Orthopedic Pillow + OrthoCurve clinical PDP).
 * Theme-native port of the WON Intelligems variant (v1). Originally mobile-only;
 * runs at ALL widths since the Aug 2026 desktop-parity rollout (the desktop
 * behavior itself was validated in the Aug 2026 Intelligems DCOP desktop test).
 *
 * Fidelity notes:
 *   - The marketing slides are ordinary gallery images from the product's
 *     custom.feature_gallery metafield (editable in Shopify admin), and the
 *     floating play/reverse button is server-rendered by featured-image-slider's
 *     ds09_slides branch. This file only injects the video slide — a
 *     server-rendered extra slide would break the Swiper loop — and wires the
 *     button/video behavior. It makes no assumption about how many gallery
 *     slides precede the video; the video is always appended last.
 *   - v1 won the test. The won bundle also carried a v2 thumbnail filmstrip, but
 *     it was gated behind a flag that was never set (so v2's filmstrip never ran),
 *     and v2 lost to v1 — so the filmstrip is intentionally NOT ported.
 *   - The Intelligems analytics/tracking block is dropped (stays in Intelligems).
 *
 * Asset URLs are NOT hardcoded here. The video URL is read from the
 * `#ds09-config` JSON block emitted by snippets/ds09-gallery-video.liquid; the
 * image/thumbnail URLs live in the server-rendered markup (the button's thumb
 * src is captured at init for the icon-swap behavior). Result: zero vendor-CDN
 * (CloudFront) strings survive in this compiled file.
 *
 * Scope: this script is server-side gated to the two pillow handles, so it only
 * loads on those PDPs. Width-agnostic: the same transform serves mobile and
 * desktop (styling differs per breakpoint in ds09-gallery-video.scss).
 */

const VIDEO_SLIDE_FALLBACK_INDEX = '9';
const INIT_POLL_MS = 500;
const INIT_TIMEOUT_MS = 10000;

class Ds09GalleryVideo {
  constructor(config) {
    this.config = config;
  }

  /**
   * Poll until `getEl()` returns a truthy element, or resolve null after timeout.
   *
   * Recursive setTimeout, NOT setInterval — this bundle is copied to sites
   * whose themes interfere with intervals (DosazeAU's inline "iOS scroll fix"
   * scripts filter setInterval callbacks and mass-clear intervals created in
   * the first second after DOMContentLoaded — the DS_06 lesson from #227).
   * setTimeout chains are untouched by those scripts.
   */
  static waitFor(getEl, timeout = INIT_TIMEOUT_MS) {
    return new Promise((resolve) => {
      const found = getEl();
      if (found) {
        resolve(found);
        return;
      }
      const deadline = Date.now() + timeout;
      const poll = () => {
        const el = getEl();
        if (el) {
          resolve(el);
          return;
        }
        if (Date.now() >= deadline) {
          resolve(null);
          return;
        }
        setTimeout(poll, INIT_POLL_MS);
      };
      setTimeout(poll, INIT_POLL_MS);
    });
  }

  /** Entry point: wait for the gallery Swiper to initialize, then finish it. */
  async run() {
    const swiperEl = await Ds09GalleryVideo.waitFor(
      () => document.querySelector('.feature-gallery__slider .swiper.swiper-initialized')
    );
    if (!swiperEl || !swiperEl.swiper) return;
    this.transform(swiperEl, swiperEl.swiper);
  }

  transform(swiperEl, swiper) {
    if (swiperEl.querySelector('.ds_09_new_slide')) return; // already done
    // Server didn't render DS_09 (un-gated product) — leave the gallery alone.
    // The floating button is the marker: it is emitted only under ds09_slides.
    if (!swiperEl.querySelector('.ds_09_floatingVideoIconWrapper')) return;

    this.insertVideoSlide(swiperEl, swiper);
    this.setupFloatingIcon(swiperEl, swiper);
    this.bindSlideChange(swiperEl, swiper);
  }

  /**
   * Insert the video as the final slide by recreating the Swiper loop around it.
   *
   * Every gallery slide is kept: the deck is whatever custom.feature_gallery
   * holds, so adding or removing an image in Shopify admin changes the gallery
   * and the video simply stays last. (The previous version deleted any slide
   * that had not received a hardcoded overlay, which is what made the gallery
   * un-editable from admin.)
   */
  insertVideoSlide(swiperEl, swiper) {
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    swiper.loopDestroy(); // leaves only the real slides (clones removed)
    wrapper.insertAdjacentHTML('beforeend', this.videoSlideHTML());
    swiper.loopCreate();
    swiper.update();
  }

  /**
   * Navigate to the slide whose data-swiper-slide-index matches, by resolving
   * its CURRENT DOM position and calling slideTo() on that. slideToLoop() is a
   * silent no-op on this theme's Swiper build (verified on the live desktop
   * page during the Aug 2026 test); loop mode can rotate DOM order, hence the
   * lookup on every call rather than once.
   */
  static slideToDataIndex(swiperEl, swiper, dataIndex) {
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    for (let i = 0; i < slides.length; i += 1) {
      if (slides[i].getAttribute('data-swiper-slide-index') === String(dataIndex)) {
        swiper.slideTo(i);
        return;
      }
    }
    swiper.slideToLoop(Number(dataIndex)); // fallback, should not be reached
  }

  /** Wire the server-rendered floating button (no injection). */
  setupFloatingIcon(swiperEl, swiper) {
    const iconWrapper = swiperEl.querySelector('.ds_09_floatingVideoIconWrapper');
    if (!iconWrapper) return;
    const videoIndex =
      swiperEl.querySelector('.ds_09_new_slide').getAttribute('data-swiper-slide-index') ||
      VIDEO_SLIDE_FALLBACK_INDEX;
    iconWrapper.setAttribute('data-slide-to', videoIndex);
    const iconImg = iconWrapper.querySelector('.ds_09_floatingVideoIcon_imageContainer img');
    // The Liquid sets the thumb as the button's initial image; capture it for
    // the play/reverse icon-swap behavior below.
    this.thumbUrl = iconImg.src;

    iconWrapper.addEventListener('click', () => {
      const slideTo = iconWrapper.getAttribute('data-slide-to');
      const activeSlide = swiperEl.querySelector(
        '.swiper-slide.swiper-slide-active[data-swiper-slide-index]'
      );
      if (!activeSlide) return;
      const activeIndex = activeSlide.getAttribute('data-swiper-slide-index');
      const activeImg = activeSlide.querySelector('.feature-gallery__item img');
      const activeImgSrc = activeImg && activeImg.src;
      const goingToVideo = slideTo === videoIndex && activeIndex !== videoIndex;

      iconWrapper.classList.toggle('ds_09_reverse_active', goingToVideo);
      Ds09GalleryVideo.slideToDataIndex(swiperEl, swiper, slideTo);

      if (goingToVideo) {
        const video = swiperEl.querySelector('.ds_09_new_slide video');
        video.currentTime = 0;
        video.play();
      }

      iconImg.src = activeIndex === videoIndex ? this.thumbUrl : activeImgSrc;
      iconWrapper.setAttribute('data-slide-to', activeIndex);
    });
  }

  /** Keep the button + video in sync as the user swipes the gallery. */
  bindSlideChange(swiperEl, swiper) {
    const videoIndex = Number(
      swiperEl.querySelector('.ds_09_new_slide').getAttribute('data-swiper-slide-index') ||
        VIDEO_SLIDE_FALLBACK_INDEX
    );
    const iconWrapper = swiperEl.querySelector('.ds_09_floatingVideoIconWrapper');
    const iconImg = iconWrapper.querySelector('.ds_09_floatingVideoIcon_imageContainer img');

    swiper.on('slideChange', () => {
      const index = swiper.realIndex;
      const video = swiperEl.querySelector('.ds_09_new_slide video');

      if (index !== videoIndex) {
        // Left the video slide: reset the button, pause + rewind the video.
        iconWrapper.classList.remove('ds_09_reverse_active');
        iconImg.src = this.thumbUrl;
        iconWrapper.setAttribute('data-slide-to', videoIndex);
        if (video && !video.paused) {
          video.pause();
          video.currentTime = 0;
        }
      } else if (index === videoIndex && swiper.previousRealIndex !== videoIndex) {
        // Arrived at the video slide by swiping: autoplay from the start.
        if (video && video.paused) {
          video.currentTime = 0;
          video.play();
        }
      }
    });
  }

  videoSlideHTML() {
    return `<div class="swiper-slide ds_09_new_slide">
      <div class="ds_09_new_slide_container">
        <video playsinline controls>
          <source src="${this.config.videoUrl}" type="video/mp4"/>
        </video>
      </div>
    </div>`;
  }
}

(function initDs09() {
  const configEl = document.getElementById('ds09-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config || typeof config.videoUrl !== 'string' || !config.videoUrl) return;

  new Ds09GalleryVideo(config).run();
})();
